// VASValueFeedback.jsx

import React from 'react';
import VASGraph from './VASGraph';
import { useTranslation } from 'react-i18next';
import { variableTranslations } from '../../constants/variableTranslations';

const VASValueFeedback = ({
  userData = {},
  assessments = [],
  sessionEvaluations = [],
  consensusEvaluation = [],
  attributeKeys,
  labelT = null,
  allVoicedLegendLabel = null,
}) => {
  const { t } = useTranslation();
  const feedbackT = labelT || t;
  const translateLabel = feedbackT;

  const excludedAttributes = ['pitch', 'loudness'];

  const attributeOrder = [
    'overallSeverity', 'roughness', 'breathiness', 'strain',
    'aphonicBreak', 'pitchBreak', 'pitchInstability', 'spasm', 'tremor',
    'aphonia', 'asthenia', 'diplophonia', 'falsetto', 'fry', 'hardGlottalAttack', 'wetGurgly',
  ];

  const processVASData = ({
    focalData = userData,
    sharedAssessments = assessments,
    participantAssessments = sessionEvaluations,
    submittedConsensus = consensusEvaluation,
    keys = attributeKeys,
  } = {}) => {
    const vasData = new Map();

    const addEntry = (
      source,
      key,
      value,
      raterSource = null,
      feedbackPresentation = null
    ) => {
      const VASValue = value?.VASValue;
      const attributeName = value?.attributeName || key;
      if (typeof VASValue !== 'number' || isNaN(VASValue) || excludedAttributes.includes(key)) return;
      if (!vasData.has(attributeName)) {
        vasData.set(attributeName, {
          key,
          user: [],
          shared: [],
          session: [],
          consensus: [],
        });
      }
      const entry = { value: VASValue };
      if (raterSource) entry.raterSource = raterSource;
      if (feedbackPresentation) entry.feedbackPresentation = feedbackPresentation;
      vasData.get(attributeName)[source].push(entry);
    };

    Object.entries(focalData?.perceptualData || {}).forEach(([key, value]) => {
      if (keys?.includes(key)) {
        addEntry('user', key, value);
      }
    });

    participantAssessments.forEach(evaluation => {
      Object.entries(evaluation?.perceptualData || {}).forEach(([key, value]) => {
        if (keys?.includes(key)) {
          addEntry('session', key, value);
        }
      });
    });

    if (submittedConsensus?.perceptualData) {
      Object.entries(submittedConsensus.perceptualData).forEach(([key, value]) => {
        if (keys?.includes(key)) {
          addEntry('consensus', key, value);
        }
      });
    }

    sharedAssessments.forEach(assessment => {
      const raterSource = assessment.raterSource || 'unknownSource';
      Object.entries(assessment?.perceptualData || {}).forEach(([key, value]) => {
        if (keys?.includes(key)) {
          addEntry(
            'shared',
            key,
            value,
            raterSource,
            assessment.feedbackPresentation || null
          );
        }
      });
    });

    return Array.from(vasData.entries())
      .sort(([aName, aData], [bName, bData]) => {
        const indexA = attributeOrder.indexOf(aData.key);
        const indexB = attributeOrder.indexOf(bData.key);
        return (indexA !== -1 ? indexA : 999) - (indexB !== -1 ? indexB : 999);
      })
      .map(([attributeName, values]) => {
        const isTranslatable = variableTranslations.hasOwnProperty(attributeName);
        const displayName = isTranslatable
          ? translateLabel(variableTranslations[attributeName])
          : attributeName;

        return {
          key: values.key,
          attributeName,
          displayName,
          userValues: values.user,
          sharedValues: values.shared,
          sessionValues: values.session,
          consensusValues: values.consensus,
        };
      });
  };

  const vasData = processVASData();
  return (
    <div className="feedback-section">
      {vasData.length > 0 ? (
        vasData.map(({ attributeName, displayName, key, userValues, sharedValues, sessionValues, consensusValues }) => (
          <div key={attributeName} className="vas-graph" data-feedback-comparison-item={key}>
            <p className="small">{displayName}</p>
            <VASGraph
              attributeName={attributeName}
              userValues={userValues}
              sharedValues={sharedValues}
              sessionValues={sessionValues}
              consensusValues={consensusValues}
              labelT={feedbackT}
              allVoicedLegendLabel={allVoicedLegendLabel}
            />
          </div>
        ))
      ) : (
        <p style={{ textAlign: 'center', fontSize: '0.7em' }}>{feedbackT('no_vas_data_available')}</p>
      )}
    </div>
  );
};

export default VASValueFeedback;
